import type { SelectOption } from '@components';
import type { SxProps, Theme } from '@mui/material';
import type { Query, QueryKey } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, useState } from 'react';

import { useDebounceValue } from '@core/hooks/useDebounceValue';
import { ConstructListQueryParams } from '@core/utils/lookupQuery';
import type { NestedKeyOf } from '@core/utils/types';

import { type UseSlabQueryType, useSlabQuery } from '../../hooks/useSlabQuery';
import type { QueryRouteBarrelTypes } from '../../utils/ApiClient';
import type { QueryError } from '../../utils/Query';
import { MultiLookupInput } from './MultiLookupInput';
import { AlertDupeOptions, ErrorOption, LoadingOption, type RouteHelpers } from './utils';

type ApiMultiLookupInputProps<
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
  QOptions extends UseSlabQueryType<QKey, QBarrel>['options'] = UseSlabQueryType<
    QKey,
    QBarrel
  >['options'],
> = {
  formState: FState;
  name: FPath;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  tip?: string;
  sx?: SxProps<Theme>;
  omitEmptyHelperText?: boolean;
  route: {
    barrel: QKey;
    args: (inputText: string, helpers: RouteHelpers<QKey>) => QBarrel['args'];
    options?: QOptions;
    transform: (d: QBarrel['returns']) => SelectOption<string>[];
  };
  onMatchChange?: (matches: SelectOption<string>[]) => void;
  /** Function to match form values to options */
  formValueMatcher: (
    formValue: { id: string }[],
    options: SelectOption<string>[],
  ) => SelectOption<string>[];
  /** custom render function for the value of the input. */
  renderValue?: (selected: SelectOption<string>[]) => ReactNode;
  withCheckbox?: boolean;
};

/** An API-based multi-select lookup input component that integrates with Formik. */
export const ApiMultiLookupInput = <
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
  QOptions extends UseSlabQueryType<QKey, QBarrel>['options'] = UseSlabQueryType<
    QKey,
    QBarrel
  >['options'],
>({
  route,
  onMatchChange = undefined,
  formValueMatcher,
  ...props
}: ApiMultiLookupInputProps<FState, FPath, QKey, QBarrel, QOptions>): ReactElement => {
  const [lookupText, setLookupText] = useState('');
  const debouncedLookupText = useDebounceValue(lookupText, 200);

  const helpers: RouteHelpers<QKey> = { constructQueryParams: ConstructListQueryParams };

  const routeArgs = route.args(debouncedLookupText, helpers);

  const routeEnabled = route.options?.enabled;
  const enabled =
    typeof routeEnabled === 'function'
      ? (query: Query<QBarrel['returns'], QueryError, QBarrel['returns'], QueryKey>): boolean =>
          routeEnabled(query)
      : (routeEnabled ?? true);

  const { isLoading, isError, data } = useSlabQuery(route.barrel, routeArgs, {
    // refetchInterval must be explicit for type compilation when spreading.
    refetchInterval: route.options?.refetchInterval,
    ...route.options,
    enabled,
  });

  const options = ((): SelectOption<string>[] => {
    if (isLoading) {
      return [LoadingOption];
    }
    if (isError) {
      return [ErrorOption];
    }
    // If there is no data, it is assumed that transform will not append options.
    if (data === undefined) {
      return [];
    }
    const opts = route.transform(data);
    AlertDupeOptions(opts);
    return opts;
  })();

  return (
    <MultiLookupInput
      {...props}
      options={options}
      inputValue={lookupText}
      onInputValueChange={setLookupText}
      onMatchChange={onMatchChange ?? ((): void => {})}
      formValueMatcher={formValueMatcher}
      disableClientSideFiltering
    />
  );
};
